import { useOverlayState } from "@heroui/react";
import { CalendarProvider } from "@upsquad/shared";
import { Calendar } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button, Chip, Modal, Spinner } from "@/src/common/components/ui";
import { env } from "@/src/common/constants/env";
import { toastService } from "@/src/common/services/toast.service";
import { useGetCalendarConnectionsQuery, useUnlinkCalendarMutation } from "../services/member-http.service";

interface CalendarSyncModalProps {
	isOpen: boolean;
	onClose: () => void;
}

const PROVIDERS = [
	{ key: CalendarProvider.GOOGLE, label: "calendar.google" },
	{ key: CalendarProvider.OUTLOOK, label: "calendar.outlook" },
] as const;

export function CalendarSyncModal({ isOpen, onClose }: CalendarSyncModalProps) {
	const { t } = useTranslation("member");
	const state = useOverlayState({ isOpen, onOpenChange: (open) => !open && onClose() });

	const { data, isLoading } = useGetCalendarConnectionsQuery(undefined, { skip: !isOpen });
	const [unlinkCalendar, { isLoading: isUnlinking }] = useUnlinkCalendarMutation();

	const connections = data?.connections ?? [];

	const handleConnect = (provider: CalendarProvider) => {
		window.open(`${env.SERVER_URL}/calendar/connect/${provider}`, "_self");
	};

	const handleUnlink = async (connectionId: string) => {
		try {
			await unlinkCalendar(connectionId).unwrap();
			toastService.success(t("calendar.unlink_success"));
		} catch {
			toastService.error(t("calendar.unlink_error"));
		}
	};

	return (
		<Modal state={state}>
			<Modal.Backdrop>
				<Modal.Container>
					<Modal.Dialog>
						<Modal.Header>
							<h2 className="text-lg font-semibold">{t("calendar.title")}</h2>
						</Modal.Header>
						<Modal.Body>
							{isLoading ? (
								<div className="flex justify-center py-8">
									<Spinner size="lg" />
								</div>
							) : (
								<div className="space-y-3">
									{PROVIDERS.map(({ key, label }) => {
										const connection = connections.find((c) => c.provider === key);
										const isConnected = !!connection;

										return (
											<div key={key} className="flex items-center justify-between rounded-lg border border-divider p-4">
												<div className="flex items-center gap-3">
													<Calendar size={20} className="text-foreground/60" />
													<span className="text-sm font-medium">{t(label)}</span>
												</div>
												<div className="flex items-center gap-2">
													{isConnected ? (
														<>
															<Chip size="sm" color="success">
																{t("calendar.connected")}
															</Chip>
															<Button size="sm" variant="outline" onPress={() => handleUnlink(connection.id)} isPending={isUnlinking}>
																{t("calendar.unlink")}
															</Button>
														</>
													) : (
														<Button size="sm" variant="primary" onPress={() => handleConnect(key)}>
															{t("calendar.connect")}
														</Button>
													)}
												</div>
											</div>
										);
									})}
								</div>
							)}
						</Modal.Body>
						<Modal.Footer className="flex justify-end">
							<Button variant="ghost" onPress={onClose}>
								{t("profile.cancel")}
							</Button>
						</Modal.Footer>
					</Modal.Dialog>
				</Modal.Container>
			</Modal.Backdrop>
		</Modal>
	);
}
